import type { Dispatch, SetStateAction } from "react";
import { useEffect } from "react";

interface UseDropdownPositionLifecycleInput {
  isOpen: boolean;
  measuredHeight: number | null;
  setMeasuredHeight: Dispatch<SetStateAction<number | null>>;
  updateDropdownPosition: () => void;
}

function useDropdownInitialPositionFrame({
  isOpen,
  updateDropdownPosition,
}: {
  isOpen: boolean;
  updateDropdownPosition: () => void;
}): void {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const positionRaf = window.requestAnimationFrame(() => {
      updateDropdownPosition();
    });

    return () => {
      window.cancelAnimationFrame(positionRaf);
    };
  }, [isOpen, updateDropdownPosition]);
}

function useDropdownMeasuredPositionFrame({
  isOpen,
  measuredHeight,
  updateDropdownPosition,
}: {
  isOpen: boolean;
  measuredHeight: number | null;
  updateDropdownPosition: () => void;
}): void {
  useEffect(() => {
    if (!isOpen || measuredHeight === null) {
      return;
    }

    const revealRaf = window.requestAnimationFrame(() => {
      updateDropdownPosition();
    });

    return () => {
      window.cancelAnimationFrame(revealRaf);
    };
  }, [isOpen, measuredHeight, updateDropdownPosition]);
}

function useDropdownPositionListeners({
  isOpen,
  updateDropdownPosition,
}: {
  isOpen: boolean;
  updateDropdownPosition: () => void;
}): void {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePositionUpdate = () => {
      updateDropdownPosition();
    };

    window.addEventListener("scroll", handlePositionUpdate, true);
    window.addEventListener("resize", handlePositionUpdate);

    return () => {
      window.removeEventListener("scroll", handlePositionUpdate, true);
      window.removeEventListener("resize", handlePositionUpdate);
    };
  }, [isOpen, updateDropdownPosition]);
}

function useDropdownMeasuredHeightReset({
  isOpen,
  setMeasuredHeight,
}: {
  isOpen: boolean;
  setMeasuredHeight: Dispatch<SetStateAction<number | null>>;
}): void {
  useEffect(() => {
    if (isOpen) {
      return;
    }

    const resetFrame = window.requestAnimationFrame(() => {
      setMeasuredHeight(null);
    });

    return () => {
      window.cancelAnimationFrame(resetFrame);
    };
  }, [isOpen, setMeasuredHeight]);
}

export function useDropdownPositionLifecycle({
  isOpen,
  measuredHeight,
  setMeasuredHeight,
  updateDropdownPosition,
}: UseDropdownPositionLifecycleInput): void {
  useDropdownInitialPositionFrame({ isOpen, updateDropdownPosition });
  useDropdownMeasuredPositionFrame({
    isOpen,
    measuredHeight,
    updateDropdownPosition,
  });
  useDropdownPositionListeners({ isOpen, updateDropdownPosition });
  useDropdownMeasuredHeightReset({ isOpen, setMeasuredHeight });
}
